import { useEffect, useRef } from 'react';
import { hasValue, isFunction } from 'swipeclock-frontend';
import { readOfflinePunchQueue } from '../utils/offlinePunchStorage';
import { purgeExpiredItems } from '../utils/offlinePunchQueueUtils';
import { confirmOfflineWebClockCleanup } from '../utils/confirmOfflineWebClockCleanup';
import { markOfflineClockCacheHydrationSettled } from '../utils/offlineClockConnectCache';
import {
  hydrateOfflineWebClockStorageForUser,
  isOfflineSecureStorageWritable,
  resolvePendingOfflineWebClockClear,
  setPendingOfflineWebClockClear,
  waitForPendingOfflineWebClockClear,
} from '../utils/offlineWebClockStorage';

const lastKnownOfflineUserKeyRef = { current: null };

/**
 * Last userKey observed while OfflinePunchProvider was mounted.
 * Used by token-invalidation cleanup when Redux no longer has site/employeeId.
 *
 * @returns {string | null}
 */
export const getLastKnownOfflineWebClockUserKey = () =>
  lastKnownOfflineUserKeyRef.current;

const resolveClearKey = (sessionKey) =>
  hasValue(sessionKey) ? sessionKey : lastKnownOfflineUserKeyRef.current;

const clearOfflineDataWithWarning = async (userKey) => {
  const { cleared, pendingKey } = await confirmOfflineWebClockCleanup(userKey);

  if (!cleared) {
    setPendingOfflineWebClockClear(pendingKey);
    console.warn(
      '[useOfflineWebClockLifecycle] Offline WebClock cleanup was not confirmed.'
    );
  }

  return cleared;
};

const failClosedUnconfirmedCleanup = ({
  authBlockedRef,
  setQueue,
  onUnconfirmedSessionCleanupRef,
}) => {
  setQueue([]);
  authBlockedRef.current = true;

  if (isFunction(onUnconfirmedSessionCleanupRef.current)) {
    onUnconfirmedSessionCleanupRef.current();
  }
};

const releaseAwaitingAuthItems = (queue, retryNow = Date.now()) => {
  const nextQueue = queue.map((item) =>
    item.awaitingAuth
      ? { ...item, awaitingAuth: false, retryAfter: retryNow }
      : item
  );
  const didUnblock = nextQueue.some((item, index) => item !== queue[index]);

  return { nextQueue, didUnblock };
};

const persistReleasedAwaitingAuthQueue = async (
  persistQueue,
  sessionKey,
  queue
) => {
  const released = releaseAwaitingAuthItems(queue);

  if (released.didUnblock) {
    await persistQueue(released.nextQueue, sessionKey);
  }

  return released;
};

/**
 * Offline storage lifecycle: user-key changes, secure-storage hydration,
 * and session invalidation (token cleared / remount without a session /
 * token refresh). An auth error only blocks drain so the same employee's
 * queue can sync after a valid token returns.
 */
export function useOfflineWebClockLifecycle({
  authBlockedRef,
  authError,
  hydrationPromiseRef,
  isOfflineShellMode = true,
  isOnlineRef,
  onUnconfirmedSessionCleanup,
  persistQueue,
  runQueueOperation,
  scheduleDrain,
  setQueue,
  userKey,
  userKeyRef,
  userToken,
}) {
  const previousUserKeyRef = useRef(userKey);
  const previousTokenRef = useRef(userToken);
  const previousAuthErrorRef = useRef(authError);
  const userTokenRef = useRef(userToken);
  const userChangeCleanupRef = useRef(Promise.resolve(true));
  const onUnconfirmedSessionCleanupRef = useRef(onUnconfirmedSessionCleanup);
  userTokenRef.current = userToken;
  onUnconfirmedSessionCleanupRef.current = onUnconfirmedSessionCleanup;

  if (isOfflineShellMode && hasValue(userKey)) {
    lastKnownOfflineUserKeyRef.current = userKey;
  }

  useEffect(() => {
    if (!isOfflineShellMode) {
      return undefined;
    }

    const previousUserKey = previousUserKeyRef.current;
    const userKeyChanged =
      hasValue(previousUserKey) &&
      hasValue(userKey) &&
      previousUserKey !== userKey;

    if (userKeyChanged) {
      userChangeCleanupRef.current =
        clearOfflineDataWithWarning(previousUserKey);
      setQueue([]);
    }

    previousUserKeyRef.current = userKey;

    const hydrationUserKey = userKey;
    let cancelled = false;

    const hydrateAndReload = async () => {
      try {
        await waitForPendingOfflineWebClockClear();

        const pendingResolved = await resolvePendingOfflineWebClockClear();

        if (!pendingResolved) {
          if (!cancelled) {
            failClosedUnconfirmedCleanup({
              authBlockedRef,
              setQueue,
              onUnconfirmedSessionCleanupRef,
            });
          }
          return;
        }

        const cleared = await userChangeCleanupRef.current;

        if (userKeyChanged && !cleared) {
          if (!cancelled) {
            failClosedUnconfirmedCleanup({
              authBlockedRef,
              setQueue,
              onUnconfirmedSessionCleanupRef,
            });
          }
          return;
        }

        if (
          cancelled ||
          userKeyRef.current !== hydrationUserKey ||
          authBlockedRef.current ||
          !hasValue(hydrationUserKey)
        ) {
          return;
        }

        const hydrationResult =
          hydrateOfflineWebClockStorageForUser(hydrationUserKey);

        if (isFunction(hydrationResult?.then)) {
          await hydrationResult;
        }

        if (
          cancelled ||
          userKeyRef.current !== hydrationUserKey ||
          authBlockedRef.current ||
          !hasValue(hydrationUserKey)
        ) {
          return;
        }

        if (!isOfflineSecureStorageWritable()) {
          setQueue([]);
          return;
        }

        const didUnblock = await runQueueOperation(async () => {
          if (
            cancelled ||
            userKeyRef.current !== hydrationUserKey ||
            authBlockedRef.current ||
            !hasValue(hydrationUserKey)
          ) {
            return false;
          }

          const current = purgeExpiredItems(
            readOfflinePunchQueue(hydrationUserKey)
          );
          const canRelease =
            hasValue(userTokenRef.current) && !authBlockedRef.current;

          if (!canRelease) {
            setQueue(current);
            return false;
          }

          const { didUnblock: released } =
            await persistReleasedAwaitingAuthQueue(
              persistQueue,
              hydrationUserKey,
              current
            );

          if (!released) {
            setQueue(current);
          }

          return released;
        });

        if (
          didUnblock &&
          !cancelled &&
          userKeyRef.current === hydrationUserKey &&
          !authBlockedRef.current &&
          isOnlineRef.current
        ) {
          scheduleDrain(0);
        }
      } catch {
        // Hydration failures retain the existing in-memory queue.
      } finally {
        if (
          !cancelled &&
          hasValue(hydrationUserKey) &&
          userKeyRef.current === hydrationUserKey
        ) {
          markOfflineClockCacheHydrationSettled(hydrationUserKey);
        }
      }
    };

    const hydrationPromise = hydrateAndReload();

    if (hydrationPromiseRef) {
      hydrationPromiseRef.current = hydrationPromise;
    }

    return () => {
      cancelled = true;
    };
  }, [
    authBlockedRef,
    hydrationPromiseRef,
    isOfflineShellMode,
    isOnlineRef,
    persistQueue,
    runQueueOperation,
    scheduleDrain,
    setQueue,
    userKey,
    userKeyRef,
  ]);

  useEffect(() => {
    if (!isOfflineShellMode) {
      return;
    }

    const previousToken = previousTokenRef.current;
    const previousAuthError = previousAuthErrorRef.current;
    previousTokenRef.current = userToken;
    previousAuthErrorRef.current = authError;

    const sessionKey = userKeyRef.current;
    const authErrorActivated = Boolean(authError) && !previousAuthError;
    const tokenCleared = hasValue(previousToken) && !hasValue(userToken);
    const remountedWithoutSession =
      !hasValue(previousToken) &&
      !hasValue(userToken) &&
      !hasValue(sessionKey) &&
      hasValue(lastKnownOfflineUserKeyRef.current);

    if (authErrorActivated) {
      // Same-device, same-employee auth failure: pause drain, keep the
      // queue and durable storage so the employee can sync once a valid
      // token returns, or delete the punches manually.
      authBlockedRef.current = true;
      return;
    }

    const sessionEnded = tokenCleared || remountedWithoutSession;

    if (sessionEnded) {
      const clearKey = resolveClearKey(sessionKey);
      lastKnownOfflineUserKeyRef.current = null;
      setQueue([]);

      authBlockedRef.current = false;

      (async () => {
        const cleared = await clearOfflineDataWithWarning(clearKey);

        if (!cleared && isFunction(onUnconfirmedSessionCleanupRef.current)) {
          onUnconfirmedSessionCleanupRef.current();
        }
      })();
      return;
    }

    if (!hasValue(userToken) || userToken === previousToken) {
      return;
    }

    authBlockedRef.current = false;

    if (!isOfflineSecureStorageWritable()) {
      return;
    }

    // Read + unblock + persist under the shared queue lock so a stale
    // token-refresh snapshot cannot race concurrent drain/delete writes.
    const unblockAndDrain = async () => {
      await runQueueOperation(async () => {
        if (userKeyRef.current !== sessionKey) {
          return;
        }

        const current = readOfflinePunchQueue(sessionKey);
        await persistReleasedAwaitingAuthQueue(
          persistQueue,
          sessionKey,
          current
        );
      });

      if (isOnlineRef.current) {
        scheduleDrain(0);
      }
    };

    unblockAndDrain();
  }, [
    authBlockedRef,
    authError,
    isOfflineShellMode,
    isOnlineRef,
    persistQueue,
    runQueueOperation,
    scheduleDrain,
    setQueue,
    userKeyRef,
    userToken,
  ]);
}
